/**
 * The full Settings row set of ui-ux §6.24 («Настройки»), its sanitizer and the live root application.
 * Owner: UI/Visual (F3). Storage stays F1's (`localStorage['marg.settings.v1']`, never part of the save): the screen
 * talks to a {@link SettingsPort}; {@link localSettingsPort} is a self-contained port for harnesses and for F1 to wrap.
 *
 * `UiSettings` is a superset of the frozen `DeviceSettings` contract (same keys and ranges for the shared fields;
 * volumes are 0..1), so F1's store can persist it by calling {@link sanitizeUiSettings}.
 */
import { STORAGE_KEYS, signal, type DeviceSettings, type ReadonlySignal } from '../../shared';

export interface UiSettings extends DeviceSettings {
  /** «Шрифт»: Рукописный (Alegreya) / Простой (Alegreya Sans). */
  font: 'calligraphic' | 'plain';
  /** «Тема» (P1): Как в системе / Светлая / Ночная. Forced to day until the night theme ships. */
  theme: 'system' | 'day' | 'night';
  /** «Без миниатюр» (lite mode). */
  lite: boolean;
  /** «Кадров в секунду». */
  fps: 60 | 30;
  shake: boolean; flash: boolean; highContrast: boolean; shortCinematics: boolean;
  /** «Общая громкость» 0..1 (music and sfx are DeviceSettings'). */
  master: number; mute: boolean; cues: boolean;
  /** «Скорость поединка» ×1 / ×2 (`battle.speed`, R30). */
  battleSpeed: 1 | 2; noRush: boolean; omensAuto: boolean; numbers: boolean;
  cardsFastAnim: boolean; cardsHints: boolean;
  /** «Подтверждать траты от» gold; −1 = never. */
  confirmSpend: 0 | 50 | 100 | 500 | -1;
  notify: boolean;
}

export const DEFAULT_UI_SETTINGS: UiSettings = {
  v: 1, lang: 'ru', music: 0.5, sfx: 0.8, motion: 'full', textScale: 100, quality: 'auto', haptics: true, leftHanded: false,
  font: 'calligraphic', theme: 'system', lite: false, fps: 60, shake: true, flash: true, highContrast: false, shortCinematics: false,
  master: 0.8, mute: false, cues: true, battleSpeed: 1, noRush: false, omensAuto: false, numbers: true,
  cardsFastAnim: false, cardsHints: true, confirmSpend: 100, notify: false,
};

export const TEXT_SCALES = [100, 115, 130, 150] as const;
export const CONFIRM_STEPS = [0, 50, 100, 500, -1] as const;
const QUALITIES = ['auto', 'low', 'med', 'high', 'ultra'] as const;

/** Repairs any stored object into valid settings: unknown keys dropped, bad values → `base`. Pure. */
export function sanitizeUiSettings(x: unknown, base: UiSettings = DEFAULT_UI_SETTINGS): UiSettings {
  const o = (typeof x === 'object' && x !== null ? x : {}) as Record<string, unknown>;
  const vol = (k: keyof UiSettings): number => { const v = o[k]; return typeof v === 'number' && v >= 0 && v <= 1 ? v : base[k] as number; };
  const bool = (k: keyof UiSettings): boolean => (typeof o[k] === 'boolean' ? o[k] as boolean : base[k] as boolean);
  const pick = <T>(k: keyof UiSettings, allowed: readonly T[]): T => (allowed.includes(o[k] as T) ? o[k] as T : base[k] as T);
  return {
    v: 1, lang: pick('lang', ['ru', 'en'] as const),
    music: vol('music'), sfx: vol('sfx'), master: vol('master'),
    motion: pick('motion', ['full', 'reduced', 'min'] as const),
    textScale: pick('textScale', TEXT_SCALES),
    quality: pick('quality', QUALITIES),
    haptics: bool('haptics'), leftHanded: bool('leftHanded'),
    font: pick('font', ['calligraphic', 'plain'] as const),
    theme: pick('theme', ['system', 'day', 'night'] as const),
    lite: bool('lite'), fps: pick('fps', [60, 30] as const),
    shake: bool('shake'), flash: bool('flash'), highContrast: bool('highContrast'), shortCinematics: bool('shortCinematics'),
    mute: bool('mute'), cues: bool('cues'),
    battleSpeed: pick('battleSpeed', [1, 2] as const), noRush: bool('noRush'), omensAuto: bool('omensAuto'), numbers: bool('numbers'),
    cardsFastAnim: bool('cardsFastAnim'), cardsHints: bool('cardsHints'),
    confirmSpend: pick('confirmSpend', CONFIRM_STEPS),
    notify: bool('notify'),
  };
}

/** How the Settings screen reads and writes settings (F1 adapts its `SettingsStore` to this). */
export interface SettingsPort {
  get(): UiSettings;
  set(patch: Partial<UiSettings>): void;
  readonly signal: ReadonlySignal<UiSettings>;
  /** Environment facts the screen needs to show / lock rows. */
  env?: { webgpu?: boolean; standalone?: boolean; vibrate?: boolean; fullscreen?: boolean };
  /** Optional actions of the «Данные» section (F1: save export/import, diagnostics, reset). */
  actions?: Partial<Record<'exportSave' | 'importSave' | 'copyDiagnostics' | 'resetProgress' | 'install', () => void | Promise<void>>>;
}

interface KV { getItem(k: string): string | null; setItem(k: string, v: string): void }

/** A self-contained port over `localStorage['marg.settings.v1']`: reads, sanitizes, writes, applies to `<html>`. */
export function localSettingsPort(storage: KV | null = safeStorage(), opts: { root?: HTMLElement | null; systemReducedMotion?: boolean } = {}): SettingsPort {
  let stored: unknown = null;
  try { const raw = storage?.getItem(STORAGE_KEYS.settings); stored = raw ? JSON.parse(raw) : null; } catch { stored = null; }
  const reduced = opts.systemReducedMotion ?? (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches);
  const base: UiSettings = { ...DEFAULT_UI_SETTINGS, motion: reduced ? 'reduced' : 'full', shake: !reduced, flash: !reduced };
  const s = signal(sanitizeUiSettings(stored, base));
  const root = opts.root === undefined ? (typeof document !== 'undefined' ? document.documentElement : null) : opts.root;
  if (root) applySettingsToRoot(root, s.get());
  return {
    get: () => s.get(),
    signal: s,
    set(patch) {
      const next = sanitizeUiSettings({ ...s.get(), ...patch }, s.get());
      s.set(next);
      try { storage?.setItem(STORAGE_KEYS.settings, JSON.stringify(next)); } catch (e) { console.warn('[settings] not saved', e); }
      if (root) applySettingsToRoot(root, next);
    },
  };
}

function safeStorage(): KV | null {
  try { return typeof localStorage !== 'undefined' ? localStorage : null; } catch { return null; }
}

/** Mirrors settings on `<html>` (live, no "Apply"): text scale, font, motion, left hand, contrast, theme (day until P1). */
export function applySettingsToRoot(root: HTMLElement, s: UiSettings): void {
  root.lang = s.lang;
  root.style.setProperty('--text-scale', String(s.textScale / 100));
  root.dataset.textscale = String(s.textScale);
  root.dataset.motion = s.motion;
  root.dataset.flash = s.flash ? 'on' : 'off';
  root.dataset.shake = s.shake ? 'on' : 'off';
  if (s.font === 'plain') root.dataset.font = 'plain'; else delete root.dataset.font;
  if (s.leftHanded) root.dataset.lefthand = '1'; else delete root.dataset.lefthand;
  if (s.highContrast) root.dataset.contrast = 'high'; else delete root.dataset.contrast;
  root.dataset.theme = 'day';   // «Ночной список» is P1 (ui-ux §2.2): forced day until it ships
}
